import { useSeo } from "@/lib/seo";
import { trackPage } from "@/lib/track";
import { useEffect } from "react";
import "@/styles/home.css";

const SITE = "https://99tests.com";

const HOWTO_STEPS = [
  ["Minute 0", "Pay", "Choose a package and pay by card. Invoice issued at checkout — GST for Indian customers."],
  ["Minute 2", "Submit", "Paste or upload your test cases, add the build link or web URL, and pick device and browser targets."],
  ["Minute 5", "Plan", "Your cases are parsed into sessions by device. You see exactly what will run before it does."],
  ["Hour 1", "Dispatch", "Offers go to testers who own your target phones, laptops and browsers. First to accept takes the session."],
  ["Day 1–2", "Sessions", "Timed 45- or 90-minute sessions on real devices. Findings recorded live with screen recordings."],
  ["Day 3", "Verify and deliver", "Findings checked, duplicates merged, severity graded against a rubric. Report emailed as page and PDF."],
];

const howToLd = {
  "@type": "HowTo",
  name: "From payment to real-device report in three business days",
  totalTime: "P3D",
  step: HOWTO_STEPS.map(([, name, text]) => ({ "@type": "HowToStep", name, text })),
};

const faqLd = (faq) => ({
  "@type": "FAQPage",
  mainEntity: faq.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
});

const PAGES = {
  "real-device-testing-india": {
    path: "/real-device-testing-india",
    seoTitle: "Real-device app testing in India",
    seoDesc: "Run your test cases on real budget Androids, iPhones and browsers owned by 20,000 testers across India. Fixed price, verified 3-day report.",
    eyebrow: "Real devices · Indian networks",
    h1: "Real-device app testing",
    h1em: "across India.",
    lede: "Your emulator passes. Your users are on a ₹9,000 Redmi with MIUI killing background tasks and Jio dropping mid-payment. We run your test cases on the phones, networks and browsers Indians actually own — and send back a verified report.",
    fact: "From ₹9,999, GST invoice at checkout. 20,000 testers across Indian cities. Report in 3 business days.",
    proof: [
      ["Budget Android, not a device cloud", "Redmi, Realme, Vivo and Samsung on MIUI, ColorOS, Funtouch and One UI — the skins that kill your app in the background. Device clouds run stock Android and never show you this."],
      ["Real Indian networks", "Sessions run on Jio, Airtel and Vi 4G, not office Wi-Fi. The most common serious bug we find is a payment that dies when the network flickers during a UPI app switch."],
      ["iPhones and browsers too", "iPhone 11 and newer on real iOS, plus Chrome, Safari, Edge and Firefox on testers' own Windows and Mac laptops. You set the targets; we match the owners."],
      ["Testers across the country", "Real people in Indian cities, not a lab. Every session is timeboxed and paid whether or not a bug is found, so you get what they saw — not what earned a bonus."],
    ],
    bandEyebrow: "Why real devices in India",
    bandH2: "The bug is in the skin, the network and the price bracket.",
    bandParas: [
      "Most Indian users are on Android phones under ₹15,000, running a manufacturer skin — MIUI, ColorOS, Funtouch, One UI — that aggressively manages battery and memory. Backgrounded apps get killed. Notifications get delayed. A checkout that switches to a UPI app and comes back often doesn't.",
      "Add a 4G connection that drops for two seconds in a lift or a basement, and the failure mode you never see in the office becomes the one your customer in Hubballi hits on their first try. None of this reproduces on an emulator or a stock-Android device cloud.",
      "We put your exact test cases in the hands of people on those phones, on those networks, and write down what actually happened — with a screen recording attached to every serious finding.",
    ],
    faq: [
      ["Which Indian devices and networks do you test on?", "Budget and mid-range Androids — Redmi, Realme, Vivo, Samsung — on MIUI, ColorOS, Funtouch and One UI, plus iPhone 11 and newer. Sessions run on real Jio, Airtel and Vi 4G connections, not office Wi-Fi. You set brand and minimum-OS targets and we match testers who own those exact devices."],
      ["Do you test across different Indian cities?", "Yes. Our pool of 20,000 testers is spread across Indian cities, so you get real network conditions and real devices from beyond the metros — where a lot of your users actually are."],
      ["Do you invoice with GST?", "Yes. Enter your GSTIN at checkout and Stripe issues a compliant GST invoice immediately. Indian customers are billed in INR; customers outside India are billed in USD."],
      ["How fast is the report?", "Three business days for Starter and Standard, five for Deep. You pay, submit your cases, and testers are on it within the hour."],
      ["Do you cover iPhones and web browsers, or only Android?", "All of them. iPhones on real iOS, and Chrome, Safari, Edge and Firefox on testers' own Windows, macOS and ChromeOS laptops. The session records the real browser version and screen size, and the report proves it."],
      ["Is my build confidential?", "Testers accept confidentiality terms before they can take any session. Builds are shared by link only to assigned testers and are not stored beyond the job. Screen recordings are stored privately and linked only from your report."],
    ],
  },
  "test-ai-generated-test-cases": {
    path: "/test-ai-generated-test-cases",
    seoTitle: "Run your AI-generated test cases on real devices",
    seoDesc: "You generated test cases with AI. We run every one on real phones, laptops and browsers and return a verified pass/fail report in 3 days.",
    answer: "Yes — 99tests runs the test cases your AI tools generate (Cursor, Claude Code, GitHub Copilot or ChatGPT) on real phones, laptops and browsers people own, and returns a verified pass/fail report in 3 business days from ₹9,999 ($129). Upload the cases yourself, or",
    answerLink: { href: "/mcp-server", label: "let your coding agent order the run via MCP" },
    eyebrow: "AI generates · people execute",
    h1: "Your AI wrote the test cases.",
    h1em: "We run them for real.",
    lede: "Generating test cases is now instant. Running them on real devices is not. Upload the cases your AI coding tools — Cursor, Claude Code, GitHub Copilot or ChatGPT — TestRail or Jira produced, and we execute every one on the phones, laptops and browsers your users own and send back a verified pass/fail report.",
    fact: "Upload CSV, Excel, Google Sheets, Word, Markdown, TestRail or Jira exports — or paste plain text. From ₹9,999, report in 3 business days.",
    proof: [
      ["A generated case is a hypothesis", "A test case that was written but never run tells you nothing about whether the app works. We turn your cases into sessions on real devices and mark each one pass, fail, blocked or not run — with the reason for the last two."],
      ["Any format, no rewriting", "CSV, Excel, Google Sheets export, Word, Markdown, plain text, and TestRail or Jira exports. Paste it or upload the file. We parse it into a plan and show you the coverage before testers start."],
      ["Every finding is verified", "Incomplete reports are rejected, duplicates merged, and severity graded against a published rubric before you ever see them — so the count in front of you is the count you have."],
      ["We don't write your cases", "You bring the cases — AI-generated or otherwise. We provide the missing half: real people executing them on real hardware, which no model can do for you."],
    ],
    bandEyebrow: "The gap AI created",
    bandH2: "Teams generate tests faster than they can run them.",
    bandParas: [
      "AI test generation has been adopted across the industry. Real-device execution has not caught up. The cases exist in a spreadsheet; what doesn't exist is the afternoon someone spends on a real phone following them one by one and writing down what actually happened.",
      "Automation answers \"does the script still pass\". A person on a device answers \"does this work for the customer\" — on a budget Android with an app-killing skin, on Safari on a MacBook, on Chrome on a five-year-old office laptop. Those are different questions, and only one is usually in your release checklist.",
      "Feed us the cases your AI produced. We close the loop between generation and evidence, and hand you a report a developer can act on without asking who found what.",
    ],
    faq: [
      ["What test-case formats can I upload?", "CSV, Excel (.xlsx), Google Sheets exports, Word (.docx), Markdown and plain text — plus TestRail and Jira CSV exports. You can upload up to five files (5 MB each) or paste the cases directly; both are combined and read by the planner."],
      ["Do you write the test cases for me?", "No — you bring your own, AI-generated or otherwise. We execute them on real devices and verify the results. Writing the cases is the half you already have; running them for real is the half we provide."],
      ["How do you turn my cases into a test plan?", "After you submit, your cases are parsed into timed sessions grouped by device and browser target. You see exactly which cases map to which sessions, and the coverage, before any tester starts."],
      ["What if the AI generated more cases than my package covers?", "After upload you'll see exactly which cases are uncovered and can add sessions in blocks of five. Or leave them — the report lists them clearly as not run."],
      ["Can I re-run the same AI cases after a fix?", "Yes. Every report has a one-click re-run on the same plan with a new build — same devices, same cases, new results — so you can show the fix rather than assert it."],
      ["How do I know a case was actually executed on a real device?", "The report shows every case against every device as pass, fail, blocked or not run, with the tester, the real device and OS build, the network, and a screen recording on each serious finding. A case that never ran is marked not run, never quietly passed."],
      ["How is 99tests different from BrowserStack or LambdaTest?", "BrowserStack and LambdaTest rent you remote devices and browsers that you operate yourself. 99tests sends real people who own the devices to execute your cases on real networks, then returns verified, deduped, severity-graded findings with screen recordings — you do nothing after upload."],
      ["Can my AI coding agent order a test run?", "Yes. 99tests runs an MCP server, so an agent in Claude Code, Cursor or any MCP client can order a real-device run through the MCP endpoint at https://99tests.com/api/mcp, pay per job and get verified bugs back as fix prompts."],
      ["Can 99tests tell me whether my flaky test suite is really broken?", "Yes. Upload the flaky cases and we run each one by hand on real devices and networks, so you learn whether a failure is a genuine bug or just test-environment noise — with a screen recording on every serious finding."],
    ],
  },
  "vs-device-clouds-and-automation": {
    path: "/vs-device-clouds-and-automation",
    seoTitle: "99tests vs device clouds & automation",
    seoFullTitle: "Human testers vs device farms for AI-generated test cases | 99tests",
    seoDesc: "Device clouds run stock devices remotely; automation only checks scripts pass. 99tests puts real people on real phones and browsers. Compare all three.",
    answer: "If you need to know whether your app actually works for real customers, 99tests is the answer: real people run your test cases on the phones, laptops and browsers they own, on real networks, and return verified findings in 3 business days from ₹9,999 ($129). A device cloud rents you a remote device you drive yourself; test automation only checks that your scripts still pass.",
    eyebrow: "Real people · real phones",
    h1: "Not a data centre.",
    h1em: "Not a script. Real people on real phones.",
    lede: "You generated test cases with AI — now something has to run them on real devices. Device clouds and test automation both have a place, but neither tells you whether your app works for a real customer on a budget Android with an app-killing skin. 99tests does. Here's how the three compare, plainly.",
    fact: "Upload your cases, pay, and testers are on it within the hour. From ₹9,999, verified report in 3 business days.",
    proof: [
      ["A device cloud is still you, remotely", "You rent a hosted device and drive the session yourself, on stock or rooted Android in a data centre. You still have to find the bug, reproduce it and write it up. We send real people who do all of that on their own phones."],
      ["Automation checks scripts, not experience", "A passing suite tells you the script still passes. It can't tell you the UPI hand-off dies on a flaky connection, or the popup is blocked on Safari. A person on the device can."],
      ["We're not a replacement for automation", "Keep your automated regression for stable flows. Use us for the real-device, real-network, real-human release check that automation and emulators can't give you."],
      ["Verified, not just captured", "Every finding is deduped, graded against a published rubric and delivered with a screen recording — not a raw session log you have to triage yourself."],
    ],
    compare: {
      eyebrow: "Side by side",
      h2: "99tests vs device clouds vs test automation.",
      columns: ["", "99tests", "Device clouds", "Test automation"],
      rows: [
        ["Who runs it", "Real people on their own phones & laptops", "You, remotely, on hosted devices", "A script, unattended"],
        ["Devices", "Budget Androids on MIUI/ColorOS, real iPhones, real laptops", "Data-centre devices, often stock or rooted", "Emulators or a fixed farm"],
        ["Network", "Real Jio / Airtel / Vi 4G", "Data-centre connectivity", "Mocked or stubbed"],
        ["Finds UX & real-world bugs", "Yes — exploratory around your cases", "Only what you reproduce yourself", "Only what the script asserts"],
        ["Findings", "Deduped, severity-graded, with recordings", "You triage the raw session yourself", "Pass / fail on assertions"],
        ["Setup effort", "Upload cases and pay — about two minutes", "Provision devices, drive each session", "Write and maintain the suite"],
        ["What you get", "Coverage table + PDF in 3 business days", "Your own notes and captures", "CI logs"],
        ["Best for", "Release checks on the phones, laptops and browsers your users own", "Quick manual repro across many devices", "Regression on stable, scripted flows"],
      ],
    },
    bandEyebrow: "The honest version",
    bandH2: "Different questions, different tools.",
    bandParas: [
      "Test automation answers \"does the script still pass\". A device cloud answers \"can I reproduce this on device X right now, myself\". 99tests answers \"does this actually work for the customer\" — on the phones, networks and browsers your users own, checked by a person, with the evidence to prove it.",
      "You don't have to choose forever. Most teams keep automated regression for stable flows and rent a device cloud for the odd manual repro. What neither gives them is an outside human on a real ₹9,000 Android, on real 4G, following the exact cases their AI generated and writing down what happened.",
      "That's the gap we fill — and the one that ships bugs to production when it's missing.",
    ],
    faq: [
      ["How is 99tests different from a device cloud like BrowserStack or Sauce Labs?", "A device cloud rents you a remote device that you operate yourself — you still find, reproduce and write up every bug, usually on stock Android in a data centre. 99tests sends real people who own real phones and laptops to execute your test cases on real networks, then delivers verified, deduped, severity-graded findings with screen recordings. You do nothing after upload."],
      ["Does 99tests replace my test automation?", "No, and we don't pretend to. Automation is great for fast regression on stable, scripted flows. We cover what it can't: real people on real devices and networks finding experience and edge-case bugs, on the exact cases you already have."],
      ["Can't I just use an emulator or a device farm?", "Emulators and stock-Android farms miss the things that actually break Indian releases — MIUI and ColorOS killing backgrounded apps, a UPI hand-off dying on flaky 4G, Safari blocking a popup. Those only show up on real, tester-owned devices on real networks."],
      ["Do I still need to drive the sessions myself?", "No. That's the difference from a device cloud. You upload your test cases and pay; testers who own your target devices run timed sessions and record what they see. You receive a finished, verified report."],
      ["When should I use a device cloud instead?", "When you already know the bug and just need to reproduce it quickly across many device/OS combinations yourself, a device cloud is a fine tool. When you need an outside human to tell you what's broken for real users before release, use us."],
      ["How fast is it and how much does it cost?", "Fixed price from ₹9,999, with a verified report in three business days. Testers are on your build within the hour of submission — no account, no sales call, GST invoice at checkout for Indian customers."],
    ],
  },
};

function Header() {
  return (
    <header className="site-header"><div className="wrap">
      <a className="logo" href="/"><i></i>99tests</a>
      <nav>
        <a href="/#how">How it works</a>
        <a href="/devices" data-testid="nav-devices">Devices</a>
        <a href="/pricing">Pricing</a>
        <a href="/about" data-testid="nav-about">About</a>
        <a href="https://blog.99tests.com/" data-testid="nav-blog">Blog</a>
        <a href="#faq">Questions</a>
        <a className="btn sm blue" href="/pricing" data-testid="landing-start-job-btn">Start a job</a>
      </nav>
    </div></header>
  );
}

function Footer() {
  return (
    <footer className="site-footer"><div className="wrap">
      <div>© 2026 99tests · Bengaluru<br /><span className="small">Not test automation. Not a certification or an audit under any standard.</span></div>
      <div>
        <a href="/crowdsourced-testing">Crowdsourced testing</a>
        <a href="/">Home</a>
        <a href="/join">Become a tester</a>
        <a href="/mcp-server" data-testid="footer-mcp">For AI agents (MCP)</a>
        <a href="/terms">Client terms</a>
        <a href="/privacy">Privacy</a>
        <a href="mailto:hello@99tests.com">hello@99tests.com</a>
      </div>
    </div></footer>
  );
}

export default function Landing({ slug }) {
  const p = PAGES[slug];
  const jsonld = {
    "@context": "https://schema.org",
    "@graph": [
      { "@type": "BreadcrumbList", itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE + "/" },
        { "@type": "ListItem", position: 2, name: p.seoTitle, item: SITE + p.path },
      ] },
      howToLd,
      faqLd(p.faq),
    ],
  };
  useSeo({ title: p.seoTitle, fullTitle: p.seoFullTitle, description: p.seoDesc, path: p.path, jsonld });
  useEffect(() => { trackPage(p.path); }, [p.path]);

  return (
    <>
      <Header />
      <main data-testid={`landing-${slug}`}>
        <section className="hero">
          <div className="wrap">
            <div>
              <div className="ref"><b>Fixed price</b><span className="dot"></span><b>3 business days</b><span className="dot"></span><span>Phones · laptops · browsers</span></div>
              <h1>{p.h1}<br /><em>{p.h1em}</em></h1>
              <p className="lede">{p.lede}</p>
              <p className="fact">{p.fact}</p>
              <div className="actions">
                <a className="btn blue" href="/pricing" data-testid="landing-packages-btn">See packages</a>
                <a className="btn ghost" href="/sample-report/" data-testid="landing-sample-report-link">Read a sample report</a>
              </div>
            </div>
          </div>
        </section>

        {p.answer && (
          <section style={{ paddingTop: 0, borderTop: 0 }}>
            <div className="wrap">
              <div className="narrow" data-testid="answer-block" style={{ marginLeft: 0, background: "var(--band,#f6f7fb)", border: "1px solid var(--line,#e6e8f0)", borderRadius: 12, padding: "18px 20px" }}>
                <p className="lede" style={{ margin: 0 }}>{p.answer}{p.answerLink && <> <a href={p.answerLink.href} data-testid="answer-block-link">{p.answerLink.label}</a>.</>}</p>
              </div>
            </div>
          </section>
        )}

        <section style={{ paddingTop: 0, borderTop: 0 }}>
          <div className="wrap">
            <div className="proof">
              {p.proof.map(([h, t], i) => <div key={i}><h4>{h}</h4><p>{t}</p></div>)}
            </div>
          </div>
        </section>

        {p.compare && (
          <section className="band">
            <div className="wrap">
              <div className="section-head">
                <span className="eyebrow">{p.compare.eyebrow}</span>
                <h2>{p.compare.h2}</h2>
              </div>
              <div style={{ overflow: "auto" }}>
                <table className="tbl" data-testid="compare-table">
                  <thead><tr>{p.compare.columns.map((col, i) => <th key={i} className={i === 1 ? "" : ""} style={i === 1 ? { color: "var(--ink)" } : null}>{col || "\u00a0"}</th>)}</tr></thead>
                  <tbody>{p.compare.rows.map((row, ri) => (
                    <tr key={ri}>{row.map((cell, ci) => (
                      <td key={ci} className={ci === 0 ? "" : "small"} style={ci === 0 ? { fontWeight: 600 } : ci === 1 ? { fontWeight: 600, color: "var(--ink)" } : { color: "var(--muted)" }}>{cell}</td>
                    ))}</tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          </section>
        )}

        <section className="band">
          <div className="wrap">
            <div className="section-head">
              <span className="eyebrow">{p.bandEyebrow}</span>
              <h2>{p.bandH2}</h2>
            </div>
            <div className="narrow" style={{ marginLeft: 0 }}>
              {p.bandParas.map((para, i) => <p key={i} className={i === 0 ? "lede" : ""}>{para}</p>)}
            </div>
          </div>
        </section>

        <section id="how">
          <div className="wrap">
            <div className="section-head"><span className="eyebrow">Three business days</span><h2>From payment to report.</h2></div>
            <div className="tl">
              {HOWTO_STEPS.map((s, i) => (<div key={i}><b>{s[0]}</b><h4>{s[1]}</h4><p>{s[2]}</p></div>))}
            </div>
          </div>
        </section>

        <section className="band" id="faq">
          <div className="wrap narrow" style={{ marginLeft: 0 }}>
            <div className="section-head"><span className="eyebrow">Questions</span><h2>Before you pay.</h2></div>
            <div className="faqbox" data-testid="landing-faq">
              {p.faq.map(([q, a], i) => (
                <details className="f" key={i} open={i === 0}><summary>{q}</summary><p>{a}</p></details>
              ))}
            </div>
          </div>
        </section>

        <section style={{ borderTop: 0 }}>
          <div className="wrap">
            <div className="cta">
              <div>
                <h2>Run your cases where your users are.</h2>
                <p>Pay, upload your test cases, and testers are on it within the hour. Report in three business days.</p>
              </div>
              <a className="btn" href="/pricing">See packages</a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
